/**
 * O5 · Program resolution for Cardmoji's own prepaid surface (/create).
 *
 * Public-safe projection only (O4). No bank tenant is resolved on this path.
 */
import { supabase } from "@/integrations/supabase/client";

/** The launch program for Cardmoji's prepaid funnel. Not a URL segment — the
 *  consumer surface is Cardmoji-owned and carries no tenant in the path. */
export const LAUNCH_PROGRAM_SLUG = "cardmoji-consumer";

/** One gallery tile = one THEME row (`hobbies`) exposed by the programme. */
export interface PublicProgramStyle {
  /** Public tile slug (`hobbies.gallery_slug`), stored on card_requests.style. */
  style_id: string;
  /** `hobbies.id` — the theme layer of the prompt. */
  theme_id?: string | null;
  /** Default sub-style bucket for the theme (dogs | cats | sports | hobbies). */
  sub_style_id?: string | null;
  display_name?: string | null;
  emoji?: string | null;
  category?: string | null;
  color?: string | null;
  /** B3 · allocated gallery preview (public bytes in `generated-avatars`). */
  preview_image_url?: string | null;
}

export interface PublicProgram {
  program_id: string;
  slug: string;
  display_name: string;
  brand_primary_color: string | null;
  brand_logo_url: string | null;
  card_network: string | null;
  price_cents: number | null;
  currency: string | null;
  diligence_level: string | null;
  diligence_copy_key: string | null;
  consents_required: string[];
  styles: PublicProgramStyle[];
  fulfiller_id: string | null;
  fulfiller_configured: boolean;
  /** Sanitised program product config (allowlisted server-side). */
  config: Record<string, any>;
}

export async function getPublicProgramBySlug(slug: string) {
  const { data, error } = await supabase.rpc("get_public_program_by_slug" as any, {
    p_slug: slug,
  });
  if (error) return { data: null, error };
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return { data: null, error: null };
  const p = row as any;
  const program: PublicProgram = {
    program_id: p.program_id,
    slug: p.slug,
    display_name: p.display_name,
    brand_primary_color: p.brand_primary_color ?? null,
    brand_logo_url: p.brand_logo_url ?? null,
    card_network: p.card_network ?? null,
    price_cents: p.price_cents ?? null,
    currency: p.currency ?? null,
    diligence_level: p.diligence_level ?? null,
    diligence_copy_key: p.diligence_copy_key ?? null,
    consents_required: Array.isArray(p.consents_required) ? p.consents_required : [],
    styles: Array.isArray(p.styles) ? p.styles : [],
    fulfiller_id: p.fulfiller_id ?? null,
    fulfiller_configured: Boolean(p.fulfiller_configured),
    config: (p.config && typeof p.config === "object") ? p.config : {},
  };
  return { data: program, error: null };
}
